import { useContext, useEffect, useRef } from 'react';
import { subscribe } from 'valtio';
import { LexoRank } from 'lexorank';
import { useStoreNonReactive } from '@/store';
import { UserContext } from '@/providers/UserContext';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import type { ArRowData, ArRowId } from '#types/System.Artifacts.types';
import type { CollectionPersist, CollectionState } from '@/store/Store.types';

export function useArTableOrderHydration(): void {
  const { state: rowsStateRaw, persist: rowsPersistRaw } = useStoreNonReactive('rows');
  const { isLoggedIn } = useContext(UserContext);
  const hasHydratedRef = useRef(false);
  const hasScheduledRef = useRef(false);
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    if (hasHydratedRef.current) return;
    if (!isLoggedIn) return;

    const rowsState = rowsStateRaw as CollectionState<ArRowData>;
    const rowsPersist = rowsPersistRaw as CollectionPersist<ArRowData>;

    if (rowsPersist.persistType !== 'collection') {
      return;
    }

    const clearRetryTimer = (): void => {
      const existing = retryTimerRef.current;
      if (existing) {
        clearTimeout(existing);
        retryTimerRef.current = null;
      }
    };

    const isRetryableFirestoreError = (error: unknown): boolean => {
      const code = (error as { code?: unknown } | null)?.code;
      if (typeof code !== 'string') return false;
      return (
        code === 'unavailable' ||
        code === 'deadline-exceeded' ||
        code === 'resource-exhausted' ||
        code === 'aborted' ||
        code === 'cancelled' ||
        code === 'internal'
      );
    };

    const scheduleRetry = (error: unknown): void => {
      if (!isMountedRef.current) return;
      if (!isRetryableFirestoreError(error)) return;

      const maxAttempts = 6;
      if (retryAttemptRef.current >= maxAttempts) return;

      const baseMs = 1_000;
      const maxMs = 30_000;
      const delayMs = Math.min(maxMs, baseMs * 2 ** retryAttemptRef.current);
      retryAttemptRef.current += 1;

      clearRetryTimer();
      retryTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        hasScheduledRef.current = false;
        maybeSchedule();
      }, delayMs);
    };

    const maybeSchedule = (): void => {
      if (rowsState.loading) {
        hasHydratedRef.current = false;
        hasScheduledRef.current = false;
        return;
      }
      if (hasHydratedRef.current) return;
      if (hasScheduledRef.current) return;

      const allRowIds = Array.from(rowsState.docs.keys()) as ArRowId[];
      const visibleRowIds = allRowIds.filter(
        (rowId) => rowId !== AR_TABLE_AGGREGATIONS_ROW_ID
      );

      const maxExistingOrder = visibleRowIds
        .map((rowId) => rowsState.docs.get(rowId)?.data?.order)
        .filter((order): order is string => typeof order === 'string' && order.length > 0)
        .reduce((acc, order) => (acc === null || order > acc ? order : acc), null as string | null);

      const missingRowIds = visibleRowIds
        .filter((rowId) => {
          const order = rowsState.docs.get(rowId)?.data?.order;
          return !(typeof order === 'string' && order.length > 0);
        })
        .sort((a, b) => {
          const aCreatedAt = (rowsState.docs.get(a)?.data as Record<string, unknown> | undefined)?.createdAt as {
            toMillis?: () => number;
          } | undefined;
          const bCreatedAt = (rowsState.docs.get(b)?.data as Record<string, unknown> | undefined)?.createdAt as {
            toMillis?: () => number;
          } | undefined;
          const aTime = aCreatedAt?.toMillis?.() ?? 0;
          const bTime = bCreatedAt?.toMillis?.() ?? 0;
          if (aTime !== bTime) return aTime - bTime;
          return a.localeCompare(b);
        });

      if (missingRowIds.length === 0) {
        hasHydratedRef.current = true;
        retryAttemptRef.current = 0;
        clearRetryTimer();
        return;
      }

      hasScheduledRef.current = true;

      const hydrateOrder = async (): Promise<void> => {
        const startRank = maxExistingOrder
          ? LexoRank.parse(maxExistingOrder).genNext()
          : LexoRank.middle();

        await missingRowIds.reduce(async (prevPromise, rowId) => {
          const rank = await prevPromise;
          await rowsPersist.update(
            rowId,
            { order: rank.toString() },
            { debounce: false }
          );
          return rank.genNext();
        }, Promise.resolve(startRank));

        hasHydratedRef.current = true;
        retryAttemptRef.current = 0;
        clearRetryTimer();
      };

      const runInBackground = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 100));
      runInBackground(() => {
        hydrateOrder().catch((error) => {
          console.error('ArTable order hydration failed:', error);
          hasScheduledRef.current = false;
          scheduleRetry(error);
        });
      });
    };

    const unsubscribe = subscribe(rowsState, maybeSchedule);
    maybeSchedule();
    return () => {
      isMountedRef.current = false;
      clearRetryTimer();
      unsubscribe();
    };
  }, [rowsStateRaw, rowsPersistRaw, isLoggedIn]);
}

export default useArTableOrderHydration;
